// breadcrumb.component.ts

import {
  BreadcrumbDropdownItem,
  BreadcrumbItem,
  BreadcrumbService,
} from '../../services/breadcrumb.service';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { Observable, combineLatest, map, merge, scan } from 'rxjs';

import { DataObject } from '../../models/core.models';
import { LoggerService } from '@services/logger.service';
import { Router } from '@angular/router';
import { NomDateTimeFormats } from '../../constants';

// The header's bottom row has a fixed height — filter chips beyond this cap
// collapse into a single "+N" overflow dropdown instead of wrapping.
const MAX_VISIBLE_FILTERS = 5;

interface BreadcrumbView {
  items: BreadcrumbItem[];
  overflowFilters: BreadcrumbItem[];
}

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BreadcrumbComponent implements OnInit {
  view$: Observable<BreadcrumbView> = combineLatest([
    this.breadcrumbService.getRouterItems(),
    this.breadcrumbService.getStateItems(),
    this.breadcrumbService.getCustomStateItems(),
  ]).pipe(
    map(([routerItems, stateItems, customStateItems]) => {
      const all = [...routerItems, ...stateItems, ...customStateItems];
      const filters = all.filter((item) => item.kind === 'filter');

      if (filters.length <= MAX_VISIBLE_FILTERS) {
        return { items: all, overflowFilters: [] };
      }

      const overflowFilters = filters.slice(MAX_VISIBLE_FILTERS);
      const overflowSet = new Set(overflowFilters);
      return {
        items: all.filter((item) => !overflowSet.has(item)),
        overflowFilters,
      };
    }),
  );

  NomDateTimeFormats = NomDateTimeFormats;
  currentObject: Observable<DataObject>;
  protected logger = inject(LoggerService);
  protected router = inject(Router);
  constructor(private breadcrumbService: BreadcrumbService) {}

  ngOnInit() {}

  removeOverflowFilter(item: BreadcrumbItem, event: Event): void {
    // Keep the overflow dropdown open so several filters can be removed in a row.
    event.stopPropagation();
    item.action?.(true);
  }

  onDropdownItemClick(dropdownItem: BreadcrumbDropdownItem) {
    // Handle the click event for dropdown items without a specific action
    this.logger.debug('Dropdown item clicked:', dropdownItem.label);
    // You can perform navigation or other actions here
  }

  navigate(url: string | string[] | null | undefined): void {
    // Opening/closing the menu is handled by `[suiMenuTrigger]` on the trigger
    // (it toggles on click). This only performs the navigation half.
    if (!url || (Array.isArray(url) && url.length === 0)) {
      this.logger.debug('Empty URL provided — skipping navigation.');
      return;
    }

    const targetUrl = Array.isArray(url)
      ? this.router.createUrlTree(url).toString()
      : url;
    const currentUrl = this.router.url;

    if (targetUrl !== currentUrl) {
      this.logger.debug('Navigating to new URL:', targetUrl);
      if (Array.isArray(url)) {
        this.router.navigate(url);
      } else {
        this.router.navigate([url]);
      }
    } else {
      this.logger.debug('URL is the same as current — skipping navigation.');
    }
  }
}
